import React from 'react'
import { l } from '@manychat/manyui'

import { PRICING_LINK } from 'common/billing/constants'
import { isCartPlan } from 'common/billing/entities/cart/helpers'
import {
  FreePlanCTA,
  PeriodToggle,
  PlansCTA,
  PricingLink,
  usePlansViewContext,
  useUpgradeRequestData,
} from 'common/billing/experiments'
import { getDiscountLabel } from 'common/billing/helpers/discounts'
import { useCurrentSubscription } from 'common/billing/hooks/useCurrentSubscription'

const DiscountedOfferCard = () => {
  const currentSubscription = useCurrentSubscription()
  const { offer } = useUpgradeRequestData()

  const discountedPlan = offer.find((product) => isCartPlan(product) && product.discount)

  if (!currentSubscription || !discountedPlan?.discount) {
    return null
  }

  const { name, price, discount } = discountedPlan
  const yearlySavings = Math.max(0, (price.amount - discount.price) * 12)

  return (
    <div className="d-flex flex-col gap-xs p-a-md b-a rounded-lg" data-test-id="winback-offer-card">
      <p className="text-subheading-sm">
        {l.translate('Saves you {amount}/year over your current plan', {
          amount: l.currency(yearlySavings, price.currency, 0),
        })}
      </p>
      <p className="font-medium">{name}</p>
      <div className="d-flex align-center gap-xxs">
        <span className="text-strikethrough text-secondary">
          {l.currency(price.amount, price.currency, 0)}
        </span>
        <span className="text-heading-sm">{l.currency(discount.price, price.currency, 2)}</span>
        <span>{l.translate('/mo')}</span>
      </div>
      <p className="text-body-sm text-secondary">
        {l.translate('{discountPeriod} off for {discountPeriodLength}', {
          discountPeriod: `${discount.discount}%`,
          discountPeriodLength: getDiscountLabel(discount.period, discount.periodUnit),
        })}
      </p>
      <p className="text-body-sm text-secondary">
        {l.translate('Currently on {plan} at {price}/mo', {
          plan: currentSubscription.plans[0]?.title ?? '',
          price: l.currency(
            currentSubscription.total.amount,
            currentSubscription.total.currency,
            0,
          ),
        })}
      </p>
    </div>
  )
}

export const MigrationWinbackPlansView = () => {
  const { freePlanCard, paidPlanCards } = usePlansViewContext()

  return (
    <div className="d-flex flex-col gap-md" data-test-id="winback-plan-select">
      <header className="d-flex flex-col gap-xs align-center">
        <h1 className="d-block text-center">{l.translate('HEY, DISCOUNT IS HERE 🎉')}</h1>
        <p className="text-center">
          {l.translate('Internal test mock for the win-back paywall — not production-ready.')}
        </p>
        <PeriodToggle className="d-block" />
      </header>

      <DiscountedOfferCard />

      <ul className="d-flex flex-col gap-sm">
        {freePlanCard && (
          <li className="d-flex flex-row justify-between align-center">
            <span>{freePlanCard.title}</span>
            <FreePlanCTA {...freePlanCard.ctaProps} />
          </li>
        )}
        {paidPlanCards.map((card) => (
          <li key={card.title} className="d-flex flex-row justify-between align-center">
            <span>{card.title}</span>
            <PlansCTA slug={card.slug} {...card.ctaProps} />
          </li>
        ))}
      </ul>

      <PricingLink href={PRICING_LINK} target="_blank" variant="small" className="d-inline-flex">
        {l.translate('View plan comparison table')}
      </PricingLink>
    </div>
  )
}
